/**
 * ArendaPro+ Fetch-based Client для работы с Supabase API
 * 
 * Полностью исключает создание GoTrueClient для предотвращения Multiple instances.
 * Использует только fetch API с JWT токенами из основного клиента.
 * 
 * @author ArendaPro+ Development Team
 * @version 2.0.0
 * @since 2025-08-16
 */

// ВАЖНО: НЕ импортируем supabase клиент напрямую для избежания Multiple GoTrueClient instances

// Конфигурация для Worker прокси
const WORKER_PROXY_URL = import.meta.env.VITE_SUPABASE_WORKER_URL || 'https://api.arendapro.plus';
const DIRECT_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://api.arendapro.plus';
const USE_WORKER_PROXY = (import.meta.env.VITE_USE_WORKER_PROXY || 'false') === 'true';

// Извлекаем project ref для единообразного storageKey
const extractProjectRef = (url: string): string | null => {
  try {
    const { hostname } = new URL(url);
    const [ref] = hostname.split('.') as [string, ...string[]];
    return ref || null;
  } catch {
    return null;
  }
};
const PROJECT_REF = extractProjectRef(DIRECT_SUPABASE_URL);

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Получение JWT токена только из localStorage (без обращения к supabase клиенту)
const getAuthToken = async (maxAttempts: number = 6, delayMs: number = 100): Promise<string | null> => {
  if (typeof window === 'undefined') return null;
  
  for (let i = 0; i < maxAttempts; i++) {
    try {
      // Пробуем localStorage
      const possibleKeys = [
        PROJECT_REF ? `sb-${PROJECT_REF}-auth-token` : null,
        PROJECT_REF ? `ap-worker-auth-token-${PROJECT_REF}` : null,
        'supabase.auth.token',
        'sb-auth-token'
      ].filter(Boolean) as string[];
      
      for (const storageKey of possibleKeys) {
        try {
          const raw = window.localStorage.getItem(storageKey);
          if (raw) {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            const token =
              (parsed?.currentSession as Record<string, unknown>)?.access_token as string | undefined ||
              (parsed?.access_token as string | undefined) ||
              (parsed?.user as Record<string, unknown>)?.access_token as string | undefined ||
              undefined;
            if (token) {
              if (import.meta.env.DEV) {
                console.debug('[FetchClient][token] Found token in localStorage:', storageKey);
              }
              return token;
            }
          }
        } catch {
          // ignore storage errors
        }
      }
      
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[FetchClient][token] Error getting token:', error);
      }
    }
    
    if (i < maxAttempts - 1) {
      await sleep(delayMs);
    }
  }
  
  if (import.meta.env.DEV) {
    console.warn('[FetchClient][token] No JWT token found after', maxAttempts, 'attempts');
  }
  return null;
};

// Базовый URL для API
const getApiUrl = (): string => {
  if (USE_WORKER_PROXY) {
    console.debug('[FetchClient] Using Cloudflare Worker proxy:', WORKER_PROXY_URL);
    return `${WORKER_PROXY_URL}/rest/v1`;
  } else {
    console.debug('[FetchClient] Using direct Supabase connection:', DIRECT_SUPABASE_URL);
    return `${DIRECT_SUPABASE_URL}/rest/v1`;
  }
};

// Fetch с автоматической инъекцией JWT токена
const authenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const baseUrl = getApiUrl();
  const url = `${baseUrl}/${endpoint}`;
  
  const headers = new Headers(options.headers || {});
  // apikey — обязателен для PostgREST через прокси
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!anonKey) {
    // В проде это критично — без anonKey PostgREST вернет 401
    console.warn('[FetchClient][env] VITE_SUPABASE_ANON_KEY is missing or empty');
  }
  if (!headers.has('apikey')) {
    if (anonKey) headers.set('apikey', anonKey);
  }
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');
  headers.set('X-Client-Info', 'arendapro-fetch-client');
  
  // Получаем JWT токен
  const method = (options.method || 'GET').toUpperCase();
  const isWrite = method !== 'GET';
  const token = await getAuthToken(isWrite ? 30 : 6, isWrite ? 150 : 120);
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    if (import.meta.env.DEV) {
      console.debug('[FetchClient][auth] Added JWT token to request');
    }
  } else {
    if (import.meta.env.DEV) {
      console.warn('[FetchClient][auth] No JWT token available for request:', {
        method,
        endpoint,
        isWrite
      });
    }
  }
  // Для мутаций просим PostgREST вернуть репрезентацию строки
  if (isWrite && !headers.has('Prefer')) {
    headers.set('Prefer', 'return=representation');
  }
  
  const finalOptions: RequestInit = {
    ...options,
    headers
  };
  
  // Диагностика: логируем наличие критичных заголовков (без утечки значений)
  try {
    const presentHeaders = {
      hasAuthorization: headers.has('Authorization'),
      hasApikey: headers.has('apikey'),
      prefer: headers.get('Prefer') || null
    };
    console.debug('[FetchClient][request]', {
      method,
      endpoint,
      usingProxy: USE_WORKER_PROXY,
      ...presentHeaders
    });
    // Доп. диагностика: маскируем ключи для быстрой сверки конфигурации
    const maskedApikey = headers.get('apikey')
      ? '***' + (headers.get('apikey') as string).slice(-4)
      : null;
    if (!presentHeaders.hasAuthorization || !presentHeaders.hasApikey) {
      console.warn('[FetchClient][request][missing-headers]', {
        method,
        endpoint,
        missingAuthorization: !presentHeaders.hasAuthorization,
        missingApikey: !presentHeaders.hasApikey,
        maskedApikey
      });
    }
  } catch (_) {
    // ignore diagnostics errors
  }
  
  return fetch(url, finalOptions);
};

// Supabase-совместимый интерфейс для select запросов
class FetchQueryBuilder {
  private endpoint: string;
  private selectFields: string = '*';
  private filters: string[] = [];
  private orderBy: string[] = [];
  private limitValue?: number;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  select(fields: string = '*') {
    this.selectFields = fields;
    return this;
  }

  eq(column: string, value: string | number | boolean | null) {
    this.filters.push(`${column}=eq.${value}`);
    return this;
  }

  neq(column: string, value: string | number | boolean | null) {
    this.filters.push(`${column}=neq.${value}`);
    return this;
  }

  in(column: string, values: Array<string | number | boolean>) {
    const joined = values.join(',');
    this.filters.push(`${column}=in.(${joined})`);
    return this;
  }

  is(column: string, value: string | number | boolean | null) {
    this.filters.push(`${column}=is.${value === null ? 'null' : value}`);
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    const direction = options?.ascending === false ? 'desc' : 'asc';
    this.orderBy.push(`${column}.${direction}`);
    return this;
  }

  limit(count: number) {
    this.limitValue = count;
    return this;
  }

  gte(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=gte.${value}`);
    return this;
  }

  lte(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=lte.${value}`);
    return this;
  }

  // Case-insensitive LIKE. Accepts Supabase-style patterns like %term% and converts to PostgREST *term*
  ilike(column: string, pattern: string) {
    const converted = typeof pattern === 'string' ? pattern.replace(/%/g, '*') : pattern;
    this.filters.push(`${column}=ilike.${converted}`);
    return this;
  }

  gt(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=gt.${value}`);
    return this;
  }

  lt(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=lt.${value}`);
    return this;
  }

  range(from: number, to: number) {
    this.filters.push(`limit=${to - from + 1}&offset=${from}`);
    return this;
  }

  private buildQuery(): string {
    const params = new URLSearchParams();
    params.set('select', this.selectFields);
    
    this.filters.forEach(filter => {
      const [key, value] = filter.split('=', 2);
      params.set(key, value);
    });
    
    if (this.orderBy.length > 0) {
      params.set('order', this.orderBy.join(','));
    }
    
    if (this.limitValue !== undefined) {
      params.set('limit', this.limitValue.toString());
    }
    
    return `${this.endpoint}?${params.toString()}`;
  }

  async then(
    resolve: (result: { data: unknown; error: unknown }) => void,
    reject?: (error: { data: unknown; error: unknown }) => void
  ) {
    try {
      const query = this.buildQuery();
      const response = await authenticatedFetch(query);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw error;
      }
      
      const data = await response.json();
      resolve({ data, error: null });
    } catch (error) {
      if (reject) {
        reject({ data: null, error });
      } else {
        resolve({ data: null, error });
      }
    }
  }

  async maybeSingle(): Promise<{ data: unknown; error: unknown }> {
    return new Promise((resolve, reject) => {
      this.then((result: { data: unknown; error: unknown }) => {
        if (result.error) {
          resolve(result);
          return;
        }
        
        const data = Array.isArray(result.data as unknown[]) ? (result.data as unknown[])[0] || null : result.data;
        resolve({ data, error: null });
      }, reject);
    });
  }

  async single(): Promise<{ data: unknown; error: unknown }> {
    const result = await this.maybeSingle();
    if (result.error) return result;
    
    if (!result.data) {
      return { data: null, error: { message: 'No rows found' } };
    }
    
    return result;
  }
}

// Supabase-совместимый интерфейс для insert/update/delete
class FetchMutationBuilder {
  private endpoint: string;
  private method: string;
  private payload?: unknown;
  private selectFields: string = '*';
  private filters: string[] = [];

  constructor(endpoint: string, method: string, payload?: unknown) {
    this.endpoint = endpoint;
    this.method = method;
    this.payload = payload;
  }

  select(fields: string = '*') {
    this.selectFields = fields;
    return this;
  }

  eq(column: string, value: string | number | boolean | null) {
    this.filters.push(`${column}=eq.${value}`);
    return this;
  }

  lt(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=lt.${value}`);
    return this;
  }

  lte(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=lte.${value}`);
    return this;
  }

  gt(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=gt.${value}`);
    return this;
  }

  gte(column: string, value: string | number | boolean) {
    this.filters.push(`${column}=gte.${value}`);
    return this;
  }

  async then(
    resolve: (result: { data: unknown; error: unknown }) => void,
    reject?: (error: { data: unknown; error: unknown }) => void
  ) {
    try {
      const params = new URLSearchParams();
      params.set('select', this.selectFields);
      
      // Add filters to params
      this.filters.forEach(filter => {
        const [key, value] = filter.split('=', 2);
        params.set(key, value);
      });
      
      const query = `${this.endpoint}?${params.toString()}`;
      const response = await authenticatedFetch(query, {
        method: this.method,
        body: this.payload ? JSON.stringify(this.payload) : undefined
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw error;
      }
      
      const data = await response.json();
      resolve({ data, error: null });
    } catch (error) {
      if (reject) {
        reject({ data: null, error });
      } else {
        resolve({ data: null, error });
      }
    }
  }

  async single(): Promise<{ data: unknown; error: unknown }> {
    return new Promise((resolve, reject) => {
      this.then((result: { data: unknown; error: unknown }) => {
        if (result.error) {
          resolve(result);
          return;
        }
        
        const data = Array.isArray(result.data as unknown[]) ? (result.data as unknown[])[0] || null : result.data;
        if (!data) {
          resolve({ data: null, error: { message: 'No rows found' } });
          return;
        }
        
        resolve({ data, error: null });
      }, reject);
    });
  }

  async maybeSingle(): Promise<{ data: unknown; error: unknown }> {
    return new Promise((resolve, reject) => {
      this.then((result: { data: unknown; error: unknown }) => {
        if (result.error) {
          resolve(result);
          return;
        }
        
        const data = Array.isArray(result.data as unknown[]) ? (result.data as unknown[])[0] || null : result.data;
        resolve({ data, error: null });
      }, reject);
    });
  }
}

// Основной fetch-based клиент
class FetchClient {
  from(table: string) {
    return {
      select: (fields?: string) => new FetchQueryBuilder(table).select(fields),
      insert: (data: unknown) => new FetchMutationBuilder(table, 'POST', data),
      update: (data: unknown) => new FetchMutationBuilder(table, 'PATCH', data),
      delete: () => new FetchMutationBuilder(table, 'DELETE'),
      upsert: (data: unknown) => new FetchMutationBuilder(table, 'POST', data)
    };
  }

  functions = {
    invoke: async (functionName: string, options?: { body?: unknown }) => {
      const ref = PROJECT_REF;
      const token = (ref && (localStorage.getItem(`sb-${ref}-auth-token`) || localStorage.getItem(`ap-worker-auth-token-${ref}`))) || null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      };

      if (token) {
        try {
          const parsedToken = JSON.parse(token);
          if (parsedToken?.access_token) {
            headers['Authorization'] = `Bearer ${parsedToken.access_token}`;
          }
        } catch (e) {
          console.warn('[FetchClient] Failed to parse auth token for functions');
        }
      }

      const baseUrl = USE_WORKER_PROXY
        ? (import.meta.env.VITE_SUPABASE_WORKER_URL || 'https://arendapro-cors-proxy-prod.arendaproplus-worker.workers.dev')
        : (import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co');

      const url = `${baseUrl}/functions/v1/${functionName}`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: options?.body ? JSON.stringify(options.body) : undefined
        });

        const data = await response.json();
        
        if (!response.ok) {
          return { data: null, error: data };
        }

        return { data, error: null };
      } catch (error) {
        return { data: null, error: { message: error instanceof Error ? error.message : 'Unknown error' } };
      }
    }
  };

  rpc(functionName: string, params?: unknown) {
    return new FetchMutationBuilder(`rpc/${functionName}`, 'POST', params);
  }
}

// Экспортируем единственный экземпляр
export const fetchClient = new FetchClient();
